import React, { useState, useEffect } from "react";
import {
  Box, Typography, Paper, CircularProgress, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Chip,
  Collapse, IconButton, TextField, InputAdornment, FormControl,
  InputLabel, Select, MenuItem, Button, Menu, ListItemIcon
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import * as XLSX from "xlsx";
import { db } from "../firebase";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";

function RigaLog({ log }) {
  const [aperta, setAperta] = useState(false);

  const timestamp = log.timestamp?.toDate?.();
  const dataOra = timestamp
    ? timestamp.toLocaleString("it-IT", {
        day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit"
      })
    : "—";

  return (
    <>
      <TableRow hover sx={{ cursor: "pointer" }} onClick={() => setAperta(!aperta)}>
        <TableCell sx={{ width: 40, padding: "0 8px" }}>
          <IconButton size="small">
            {aperta ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>{dataOra}</TableCell>
        <TableCell>{log.operatore?.nome || log.operatore?.email || "—"}</TableCell>
        <TableCell>
          <Chip
            label={log.tipoOperazione}
            size="small"
            color={
              log.tipoOperazione?.includes("Creazione") ? "success" :
              log.tipoOperazione?.includes("stato") ? "primary" : "default"
            }
            variant="outlined"
          />
        </TableCell>
        <TableCell>{log.titoloEvento || "—"}</TableCell>
        <TableCell align="center">
          <Chip label={log.modifiche?.length || 0} size="small" />
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell colSpan={6} sx={{ py: 0, backgroundColor: "#fafafa" }}>
          <Collapse in={aperta} timeout="auto" unmountOnExit>
            <Box sx={{ p: 2 }}>
              {log.modifiche?.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Nessuna modifica rilevata.
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#f0f4ff" }}>
                      <TableCell><strong>Campo</strong></TableCell>
                      <TableCell><strong>Da</strong></TableCell>
                      <TableCell><strong>A</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {log.modifiche?.map((m, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{m.campo}</TableCell>
                        <TableCell sx={{ color: "error.main" }}>{m.da}</TableCell>
                        <TableCell sx={{ color: "success.main" }}>{m.a}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function Log() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ricerca, setRicerca] = useState("");
  const [filtroOperatore, setFiltroOperatore] = useState("");
  const [filtroTipo, setFiltroTipo] = useState("");
  const [menuAnchor, setMenuAnchor] = useState(null);

  useEffect(() => {
    const q = query(collection(db, "log"), orderBy("timestamp", "desc"));
    const unsub = onSnapshot(q, (snap) => {
      setLogs(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return unsub;
  }, []);

  const operatori = [...new Set(logs.map((l) => l.operatore?.nome || l.operatore?.email).filter(Boolean))];
  const tipiOperazione = [...new Set(logs.map((l) => l.tipoOperazione).filter(Boolean))];

  const logsFiltrati = logs.filter((l) => {
    const s = ricerca.toLowerCase();
    const matchRicerca = !s ||
      l.titoloEvento?.toLowerCase().includes(s) ||
      l.operatore?.nome?.toLowerCase().includes(s) ||
      l.operatore?.email?.toLowerCase().includes(s);
    const matchOperatore = !filtroOperatore ||
      (l.operatore?.nome || l.operatore?.email) === filtroOperatore;
    const matchTipo = !filtroTipo || l.tipoOperazione === filtroTipo;
    return matchRicerca && matchOperatore && matchTipo;
  });

  // Appiattisce i log per l'esportazione
  function appiattisciLogs() {
    const righe = [];
    for (const log of logsFiltrati) {
      const timestamp = log.timestamp?.toDate?.();
      const dataOra = timestamp
        ? timestamp.toLocaleString("it-IT", {
            day: "2-digit", month: "2-digit", year: "numeric",
            hour: "2-digit", minute: "2-digit", second: "2-digit"
          })
        : "";
      const operatore = log.operatore?.nome || log.operatore?.email || "";
      const email = log.operatore?.email || "";
      const tipo = log.tipoOperazione || "";
      const evento = log.titoloEvento || "";

      if (!log.modifiche || log.modifiche.length === 0) {
        righe.push({ dataOra, operatore, email, tipo, evento, campo: "", da: "", a: "" });
      } else {
        for (const m of log.modifiche) {
          righe.push({ dataOra, operatore, email, tipo, evento, campo: m.campo, da: m.da, a: m.a });
        }
      }
    }
    return righe;
  }

  function esportaTXT() {
    const righe = appiattisciLogs();
    let testo = "LOG OPERAZIONI\n";
    testo += "=".repeat(80) + "\n\n";

    let logCorrente = "";
    for (const r of righe) {
      if (r.dataOra !== logCorrente) {
        testo += `\n[${r.dataOra}] ${r.tipo}\n`;
        testo += `Operatore: ${r.operatore} (${r.email})\n`;
        testo += `Evento: ${r.evento}\n`;
        if (r.campo) testo += `Modifiche:\n`;
        logCorrente = r.dataOra;
      }
      if (r.campo) {
        testo += `  - ${r.campo}: "${r.da}" → "${r.a}"\n`;
      }
    }

    const blob = new Blob([testo], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `log_operazioni_${new Date().toISOString().split("T")[0]}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function esportaCSV() {
    const righe = appiattisciLogs();
    const intestazione = ["Data e ora", "Operatore", "Email", "Tipo operazione", "Evento", "Campo", "Da", "A"];
    const csv = [
      intestazione.join(";"),
      ...righe.map((r) =>
        [r.dataOra, r.operatore, r.email, r.tipo, r.evento, r.campo, r.da, r.a]
          .map((v) => `"${(v || "").replace(/"/g, '""')}"`)
          .join(";")
      )
    ].join("\n");

    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `log_operazioni_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function esportaXLSX() {
    const righe = appiattisciLogs();
    const dati = righe.map((r) => ({
      "Data e ora": r.dataOra,
      "Operatore": r.operatore,
      "Email": r.email,
      "Tipo operazione": r.tipo,
      "Evento": r.evento,
      "Campo": r.campo,
      "Da": r.da,
      "A": r.a,
    }));

    const ws = XLSX.utils.json_to_sheet(dati);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Log");

    // Larghezze colonne
    ws["!cols"] = [
      { wch: 18 }, { wch: 25 }, { wch: 30 }, { wch: 25 },
      { wch: 40 }, { wch: 30 }, { wch: 30 }, { wch: 30 },
    ];

    XLSX.writeFile(wb, `log_operazioni_${new Date().toISOString().split("T")[0]}.xlsx`);
  }

  if (loading) return <Box display="flex" justifyContent="center" mt={8}><CircularProgress /></Box>;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight="bold">Log operazioni</Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={(e) => setMenuAnchor(e.currentTarget)}
          >
            Esporta
          </Button>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
          >
            <MenuItem onClick={() => { esportaTXT(); setMenuAnchor(null); }}>
              Esporta TXT
            </MenuItem>
            <MenuItem onClick={() => { esportaCSV(); setMenuAnchor(null); }}>
              Esporta CSV
            </MenuItem>
            <MenuItem onClick={() => { esportaXLSX(); setMenuAnchor(null); }}>
              Esporta Excel (.xlsx)
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      <Box display="flex" gap={2} mb={3} flexWrap="wrap">
        <TextField
          placeholder="Cerca per evento o operatore..."
          value={ricerca}
          onChange={(e) => setRicerca(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
          sx={{ flex: 1, minWidth: 200 }}
          size="small"
        />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Operatore</InputLabel>
          <Select value={filtroOperatore} label="Operatore"
            onChange={(e) => setFiltroOperatore(e.target.value)}>
            <MenuItem value="">Tutti</MenuItem>
            {operatori.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Tipo operazione</InputLabel>
          <Select value={filtroTipo} label="Tipo operazione"
            onChange={(e) => setFiltroTipo(e.target.value)}>
            <MenuItem value="">Tutti</MenuItem>
            {tipiOperazione.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>

      <TableContainer component={Paper} elevation={1}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f0f4ff" }}>
              <TableCell sx={{ width: 40 }} />
              <TableCell><strong>Data e ora</strong></TableCell>
              <TableCell><strong>Operatore</strong></TableCell>
              <TableCell><strong>Operazione</strong></TableCell>
              <TableCell><strong>Evento</strong></TableCell>
              <TableCell align="center"><strong>Modifiche</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {logsFiltrati.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4, color: "text.secondary" }}>
                  Nessun log trovato
                </TableCell>
              </TableRow>
            ) : (
              logsFiltrati.map((log) => (
                <RigaLog key={log.id} log={log} />
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}